/**
 * Order Store (Zustand)
 *
 * Manages ticket selection and checkout state.
 * Used on EventPage for ticket selection flow.
 */

import { create } from "zustand";

interface TicketSelection {
  ticketTypeId: string;
  quantity: number;
}

interface OrderState {
  // Ticket selections
  selections: TicketSelection[];
  eventId: string | null;

  // Checkout state
  isCreatingOrder: boolean;
  createdOrderId: string | null;

  // Actions
  setEventId: (eventId: string) => void;
  setSelection: (ticketTypeId: string, quantity: number) => void;
  incrementQuantity: (ticketTypeId: string, maxAvailable?: number) => void;
  decrementQuantity: (ticketTypeId: string) => void;
  clearSelections: () => void;
  setCreatingOrder: (isCreating: boolean) => void;
  setCreatedOrderId: (orderId: string | null) => void;
  reset: () => void;

  // Computed (read from state)
  getTotalQuantity: () => number;
  hasSelection: () => boolean;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  selections: [],
  eventId: null,
  isCreatingOrder: false,
  createdOrderId: null,

  setEventId: (eventId) => set({ eventId, selections: [] }),

  setSelection: (ticketTypeId, quantity) =>
    set((state) => {
      if (quantity <= 0) {
        return {
          selections: state.selections.filter(
            (s) => s.ticketTypeId !== ticketTypeId
          ),
        };
      }

      const existing = state.selections.find(
        (s) => s.ticketTypeId === ticketTypeId
      );
      if (existing) {
        return {
          selections: state.selections.map((s) =>
            s.ticketTypeId === ticketTypeId ? { ...s, quantity } : s
          ),
        };
      }

      return {
        selections: [...state.selections, { ticketTypeId, quantity }],
      };
    }),

  incrementQuantity: (ticketTypeId, maxAvailable) =>
    set((state) => {
      const existing = state.selections.find(
        (s) => s.ticketTypeId === ticketTypeId
      );
      const currentQty = existing?.quantity ?? 0;
      const maxQty = maxAvailable ?? 10;

      if (currentQty >= maxQty) return state;

      if (existing) {
        return {
          selections: state.selections.map((s) =>
            s.ticketTypeId === ticketTypeId
              ? { ...s, quantity: s.quantity + 1 }
              : s
          ),
        };
      }

      return {
        selections: [...state.selections, { ticketTypeId, quantity: 1 }],
      };
    }),

  decrementQuantity: (ticketTypeId) =>
    set((state) => {
      const existing = state.selections.find(
        (s) => s.ticketTypeId === ticketTypeId
      );
      if (!existing) return state;

      if (existing.quantity <= 1) {
        return {
          selections: state.selections.filter(
            (s) => s.ticketTypeId !== ticketTypeId
          ),
        };
      }

      return {
        selections: state.selections.map((s) =>
          s.ticketTypeId === ticketTypeId
            ? { ...s, quantity: s.quantity - 1 }
            : s
        ),
      };
    }),

  clearSelections: () => set({ selections: [] }),

  setCreatingOrder: (isCreating) => set({ isCreatingOrder: isCreating }),

  setCreatedOrderId: (orderId) => set({ createdOrderId: orderId }),

  reset: () =>
    set({
      selections: [],
      eventId: null,
      isCreatingOrder: false,
      createdOrderId: null,
    }),

  getTotalQuantity: () => {
    const { selections } = get();
    return selections.reduce((sum, s) => sum + s.quantity, 0);
  },

  hasSelection: () => {
    const { selections } = get();
    return selections.some((s) => s.quantity > 0);
  },
}));
